import { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { ClockCounterClockwise, ArrowDown, ArrowUp, ArrowsLeftRight } from '@phosphor-icons/react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { useT } from '@/i18n/LanguageContext';
import { useAuth, canEditMfg } from '@/auth/AuthContext';
import { mfgApi, MOVE_TYPES, MOVE_BADGE } from './mfgApi';
import { DataTable } from '@/components/ui/DataTable';
import { formatShort } from '@/utils/datetime';
import { todayJst } from '@/utils/jstDate';

const fmt = (s) => (s ? formatShort(s) : '—');
const NONE = '__none__';
const EMPTY_TRANSFER = { item_id: '', from_department_id: '', to_department_id: '', quantity: '', reason: '' };

export default function StockMoves() {
  const { t } = useT();
  const { user } = useAuth();
  const canTransfer = canEditMfg(user);
  const [moves, setMoves] = useState([]);
  const [items, setItems] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [itemId, setItemId] = useState('all');
  const [type, setType] = useState('all');
  const [tehai, setTehai] = useState('');
  const [open, setOpen] = useState(false);
  const [tf, setTf] = useState(EMPTY_TRANSFER);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    const params = {};
    if (itemId !== 'all') params.item_id = itemId;
    if (type !== 'all') params.type = type;
    if (tehai.trim()) params.tehai_no = tehai.trim();
    mfgApi.moves(params).then(setMoves).catch(() => {});
  }, [itemId, type, tehai]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { mfgApi.items().then(setItems).catch(() => {}); }, []);
  useEffect(() => { mfgApi.departments().then(setDepartments).catch(() => {}); }, []);

  const submitTransfer = async () => {
    if (!tf.item_id) { toast.error(t('mfg.transfer.selectItem')); return; }
    if (!tf.from_department_id || !tf.to_department_id) { toast.error(t('mfg.transfer.from')); return; }
    if (tf.from_department_id === tf.to_department_id) { toast.error(t('mfg.transfer.sameDeptError')); return; }
    const qty = Number(tf.quantity);
    if (!qty || qty <= 0) { toast.error(t('mfg.transfer.qty')); return; }
    setBusy(true);
    try {
      await mfgApi.transferBetweenDepartments({
        item_id: tf.item_id, from_department_id: tf.from_department_id,
        to_department_id: tf.to_department_id, quantity: qty, reason: tf.reason || null,
      });
      toast.success(t('mfg.transfer.success'));
      setOpen(false); setTf(EMPTY_TRANSFER); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'エラーが発生しました'); }
    finally { setBusy(false); }
  };

  // CSV は見えている行をそのまま出す（絞り込みの結果と同じもの）。
  const exportCsv = () => {
    const head = ['日時', '品目', '品番', 'ロット', '種類', '数量', '単位',
      '出庫元', '入庫先', '理由', '手配番号', '担当'];
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const body = moves.map((m) => [m.at, m.item_name, m.item_sku, m.lot_no, m.type_label || m.type,
      m.qty_delta, m.uom, m.from_code, m.to_code, m.reason, m.tehai_no, m.by_name].map(esc).join(','));
    const blob = new Blob([`\uFEFF${[head.map(esc).join(','), ...body].join('\n')}`],
      { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `入出庫履歴_${todayJst()}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="space-y-5" data-testid="mfg-moves">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="text-2xl font-bold tracking-tight text-zinc-900">{t('mfg.nav.moves')}</h1>
        {canTransfer && (
          <Button data-testid="dept-transfer-btn" onClick={() => { setTf(EMPTY_TRANSFER); setOpen(true); }}
            className="h-11 rounded-lg bg-teal-700 hover:bg-teal-800 shadow-sm">
            <ArrowsLeftRight size={18} weight="bold" className="mr-1.5" /> {t('mfg.transfer.btn')}
          </Button>
        )}
      </div>

      <div className="flex gap-2 flex-wrap">
        <Select value={itemId} onValueChange={setItemId}>
          <SelectTrigger data-testid="moves-item-filter" className="h-10 w-56 rounded-lg"><SelectValue placeholder={t('mfg.moves.item')} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('mfg.moves.item')}: {t('mfg.all')}</SelectItem>
            {items.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={type} onValueChange={setType}>
          <SelectTrigger data-testid="moves-type-filter" className="h-10 w-40 rounded-lg"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('mfg.moves.type')}: {t('mfg.all')}</SelectItem>
            {MOVE_TYPES.map((m) => <SelectItem key={m} value={m}>{t(`mfg.move.${m}`)}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input value={tehai} onChange={(e) => setTehai(e.target.value)}
          placeholder="手配番号で探す（例 026100105）"
          className="h-10 w-52 rounded-lg font-mono" data-testid="moves-tehai-filter" />
        <Button variant="outline" className="h-10 rounded-lg" onClick={exportCsv}
          data-testid="moves-csv-btn">CSV</Button>
      </div>

      <DataTable
        dataTestId="moves-table"
        rows={moves}
        rowKey={(m) => m.id}
        rowTestId={(m) => `move-row-${m.id}`}
        empty={{ icon: <ClockCounterClockwise size={40} weight="duotone" className="text-zinc-300 mx-auto mb-2" />, text: t('mfg.moves.empty'), testid: 'moves-empty' }}
        columns={[
          { key: 'when', header: t('mfg.moves.when'), cell: (m) => <span className="text-zinc-500 whitespace-nowrap">{fmt(m.at)}</span> },
          { key: 'item', primary: true, header: t('mfg.moves.item'), cell: (m) => <span className="text-zinc-800">{m.item_name || '—'}<span className="block text-[11px] text-zinc-400 font-mono">{m.item_sku}</span></span> },
          { key: 'lot', header: t('mfg.lot.no'), cell: (m) => <span className="font-mono text-zinc-600">{m.lot_no || '—'}</span> },
          { key: 'type', header: '', cell: (m) => <Badge variant="outline" className={MOVE_BADGE[m.type] || ''}>{t(`mfg.move.${m.type}`)}</Badge> },
          { key: 'qty', header: t('mfg.moves.qty'), align: 'right', cell: (m) => <span className={`font-semibold tabular-nums ${m.qty_delta >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}><span className="inline-flex items-center gap-0.5">{m.qty_delta >= 0 ? <ArrowUp size={12} weight="bold" /> : <ArrowDown size={12} weight="bold" />}{m.qty_delta > 0 ? '+' : ''}{m.qty_delta} {m.uom || ''}</span></span> },
          { key: 'route', header: `${t('mfg.moves.from')} → ${t('mfg.moves.to')}`, cell: (m) => <span className="text-zinc-500 font-mono text-xs">{m.from_code || '—'} → {m.to_code || '—'}</span> },
          { key: 'tehai', header: '手配番号', cell: (m) => <span className="font-mono text-xs text-zinc-700" data-testid={`move-tehai-${m.id}`}>{m.tehai_no || m.purpose_linked_label || m.purpose_reason_label || '—'}</span> },
          { key: 'by', header: t('mfg.moves.by'), cell: (m) => <span className="text-zinc-500">{m.by_name || '—'}</span> },
        ]}
      />

      <Dialog open={open} onOpenChange={(o) => !o && setOpen(false)}>
        <DialogContent className="max-w-md" aria-describedby={undefined} data-testid="transfer-dialog">
          <DialogHeader><DialogTitle>{t('mfg.transfer.title')}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs font-semibold text-zinc-600">{t('mfg.transfer.item')}</Label>
              <Select value={tf.item_id || NONE} onValueChange={(v) => setTf((p) => ({ ...p, item_id: v === NONE ? '' : v }))}>
                <SelectTrigger data-testid="transfer-item-select" className="h-10 mt-1"><SelectValue placeholder={t('mfg.transfer.selectItem')} /></SelectTrigger>
                <SelectContent><SelectItem value={NONE} disabled>{t('mfg.transfer.selectItem')}</SelectItem>{items.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-semibold text-zinc-600">{t('mfg.transfer.from')}</Label>
                <Select value={tf.from_department_id || NONE} onValueChange={(v) => setTf((p) => ({ ...p, from_department_id: v === NONE ? '' : v }))}>
                  <SelectTrigger data-testid="transfer-from-select" className="h-10 mt-1"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent><SelectItem value={NONE} disabled>—</SelectItem>{departments.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label className="text-xs font-semibold text-zinc-600">{t('mfg.transfer.to')}</Label>
                <Select value={tf.to_department_id || NONE} onValueChange={(v) => setTf((p) => ({ ...p, to_department_id: v === NONE ? '' : v }))}>
                  <SelectTrigger data-testid="transfer-to-select" className="h-10 mt-1"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent><SelectItem value={NONE} disabled>—</SelectItem>{departments.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
            <div><Label className="text-xs font-semibold text-zinc-600">{t('mfg.transfer.qty')}</Label>
              <Input type="number" data-testid="transfer-qty-input" value={tf.quantity} onChange={(e) => setTf((p) => ({ ...p, quantity: e.target.value }))} className="h-10 mt-1" /></div>
            <div><Label className="text-xs font-semibold text-zinc-600">{t('mfg.transfer.reason')}</Label>
              <Input data-testid="transfer-reason-input" value={tf.reason} onChange={(e) => setTf((p) => ({ ...p, reason: e.target.value }))} className="h-10 mt-1" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t('mfg.cancel')}</Button>
            <Button data-testid="transfer-submit-btn" onClick={submitTransfer} disabled={busy} className="bg-teal-700 hover:bg-teal-800">{busy ? '…' : t('mfg.transfer.submit')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
